/**
 * Uses coloris.js lib for color picker:
 * https://github.com/melloware/coloris-npm
 */
import clsx from "clsx";
import { forwardRef, useEffect } from "react";

import "@melloware/coloris/dist/coloris.css";
import Coloris from "~/helpers/coloris.client";

import { debounce } from "lodash-es";

interface Props {
  className?: string,
  inputClassName?: string,
  name: string,
  defaultValue?: string,
  placeholder?: string,
  isInvalid?: boolean,
  onChange?: (value: string) => void,
  ariaLabel?: string;
  swatches?: string[];
}

const ColorInput = forwardRef<HTMLInputElement, Props>((
    {
      className = null,
      inputClassName = null,
      name,
      defaultValue = "",
      placeholder = "",
      isInvalid = false,
      swatches = [],
      onChange = () => {
      },
      ariaLabel = undefined
    }, ref) => {
    if (name.length === 0) {
      throw new Error("Empty input name");
    }

    const debouncedOnChange = debounce(onChange, 300);

    useEffect(() => {
      // @ts-ignore
      if (window.isColorisInited !== true) {
        Coloris.init();
        Coloris({
          el: `.coloris`,
          alpha: false,
          theme: 'polaroid',
          themeMode: 'auto',
        });

        // @ts-ignore
        window.isColorisInited = true;
      }

      // Set instance with specific configuration
      Coloris.setInstance(`#${name}`,{
        swatches,
        onChange: (color: string) => debouncedOnChange(color)
      });

      // Cleanup function to properly destroy the Coloris instance when component unmounts
      return () => {
        // Cancel any pending debounced onChange calls
        debouncedOnChange.cancel();
        
        // Close any open color picker to prevent memory leaks
        if (typeof Coloris.close === 'function') {
          Coloris.close();
        }
        
        // Remove the instance if there's a method for it
        if (typeof Coloris.removeInstance === 'function') {
          Coloris.removeInstance(`#${name}`);
        }
        
        // Some libraries have unsetInstance instead of removeInstance
        // Use a type assertion to handle potential alternative method
        const colorisAny = Coloris as any;
        if (!Coloris.removeInstance && typeof colorisAny.unsetInstance === 'function') {
          colorisAny.unsetInstance(`#${name}`);
        }
      };
    }, []);

    return (
      <div className={clsx(
        "relative flex items-center w-full coloris-square",
        className
      )}>
        <input
          type="text"
           id={name}
           name={name}
           className={clsx(
             "coloris block px-11",
             {
               "invalid": isInvalid
             },
             inputClassName
           )}
           placeholder={placeholder}
           defaultValue={defaultValue}
           onChange={(e) => onChange(e.target.value)}
           {...(ref ? { ref } : {})}
           aria-label={ariaLabel}
        />
      </div>
    );
  }
);
export default ColorInput;